import { useEffect, useState } from "react";
import { useCommonStore } from "state/store/common/useCommonStore";
import { useChatStore } from "state/store/chat/use-chat-store";
import { IamData } from "features/iam/services/user-service";
// import { useCollaborationContext } from "state/collaboration-websocket-middleware";
import { useCollaborationSocketStore } from "state/store/collaboration/use-collaboration-store";

export const TypingIndicator = () => {

    const currentUser = useCommonStore((state) => state.user);
    const hasParticipantsArray = useChatStore((state) => state.hasParticipantsArray);
    // const { lastNotificationMessage } = useCollaborationContext();
    // Iterate every non-streaming event in the batch — TYPING_STATUS can be
    // followed by another event in the same RAF tick and we don't want to miss
    // a "stopped typing" that arrives alongside a participant change.
    const otherMessages = useCollaborationSocketStore((state: any) => state.otherMessages);

    const [typingUserIds, setTypingUserIds] = useState<Set<string>>(new Set());
    // console.log('typingUserIds in typing indicator', typingUserIds)

    useEffect(() => {
        if (!otherMessages || otherMessages.length === 0) return;
        for (const lastNotificationMessage of otherMessages) {
            if (lastNotificationMessage && lastNotificationMessage.type === "TYPING_STATUS") {
                const typingStatus = lastNotificationMessage.response?.isTyping;
                const userIdsByType = lastNotificationMessage.userIdsByType || [];

                // Filter out current user from userIdsByType (we don't show typing indicator for current user)
                const otherUserIds = userIdsByType.filter((userId: string) => userId !== currentUser?.id);

                if (typingStatus) {
                    // User(s) started typing - add them to the typing set
                    if (otherUserIds.length > 0) {
                        setTypingUserIds((prev) => {
                            const newSet = new Set(prev);
                            otherUserIds.forEach((userId: string) => newSet.add(userId));
                            return newSet;
                        });
                    }
                } else {
                    // User(s) stopped typing
                    if (otherUserIds.length > 0) {
                        // Remove specific users who stopped typing
                        setTypingUserIds((prev) => {
                            const newSet = new Set(prev);
                            otherUserIds.forEach((userId: string) => newSet.delete(userId));
                            return newSet;
                        });
                    } else if (userIdsByType.length === 0) {
                        // If userIdsByType is empty when isTyping is false, clear all (safety fallback)
                        setTypingUserIds(new Set());
                    }
                }
            }
        }
    }, [otherMessages, currentUser?.id, hasParticipantsArray]);

    // Convert typing user IDs to user objects
    const typingUsers = hasParticipantsArray?.filter((user: IamData) => typingUserIds.has(user.itemId));
    const isTyping = typingUserIds.size > 0;

    // Get display name with fallback: first+last → email prefix → userName
    const getDisplayName = (user: IamData | undefined) => {
        if (!user) return '';
        const firstName = user.firstName?.trim();
        const lastName = user.lastName?.trim();
        const emailPrefix = typeof user.email === 'string' ? user.email.split('@')[0] : '';
        const userName = user.userName?.trim();
        return firstName || lastName || emailPrefix || userName || '';
    };

    // Format typing text based on number of users
    const getTypingText = () => {
        if (typingUsers?.length === 0) return '';

        if (typingUsers?.length === 1) {
            const displayName = getDisplayName(typingUsers[0]);
            return `${displayName} is typing`;
        } else if (typingUsers?.length === 2) {
            const displayName1 = getDisplayName(typingUsers[0]);
            const displayName2 = getDisplayName(typingUsers[1]);
            return `${displayName1} and ${displayName2} are typing`;
        } else {
            const displayName = getDisplayName(typingUsers[0]);
            const othersCount = typingUsers?.length - 1;
            return `${displayName} and ${othersCount} other${othersCount > 1 ? 's' : ''} are typing`;
        }
    };

    return isTyping ? (
        <>
            <style>{`
                @keyframes shimmer {
                    0% {
                        background-position: -200% 0;
                    }
                    100% {
                        background-position: 200% 0;
                    }
                }
                .typing-shimmer {
                    background: linear-gradient(90deg, #6b7280 0%, #9ca3af 50%, #6b7280 100%);
                    background-size: 200% 100%;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                    animation: shimmer 2s ease-in-out infinite;
                }
                .dark .typing-shimmer {
                    background: linear-gradient(90deg, #9ca3af 0%, #d1d5db 50%, #9ca3af 100%);
                    background-size: 200% 100%;
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    background-clip: text;
                }
            `}</style>
            <div className="absolute top-[-30px] left-0 right-0 flex items-center px-3 py-2 z-10 gap-2 ">
                <span className="text-xs font-normal typing-shimmer">
                    {getTypingText()}
                </span>
            </div>
        </>
    ) : <></>
}
